import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus, UserMinus, ShoppingBag, MessageSquare, Lock, Clock, Sparkles, Loader2, CheckCircle, X } from "lucide-react";
import FeedCard from "@/components/feed/FeedCard";
import { TabBar, InfoCard } from "@/components/toggles";
import RichTextView from "@/components/common/RichTextView";

// iter250 · Helper: Original-URL → Blur-Preview-URL (nutzt Server-side
// /api/blur-Endpoint, siehe storage_routes.serve_blurred_preview). Für alle
// Paid-Media-Kontexte auf Public-Profile-Seiten identisch.
function toBlurUrl(url) {
  if (!url) return url;
  if (url.startsWith("/api/blur/")) return url;
  if (url.startsWith("/api/files/")) return url.replace("/api/files/", "/api/blur/");
  return `/api/blur/${url}`;
}
import TipButton from "@/components/TipButton";
import PaymentChoiceModal from "@/components/PaymentChoiceModal";
import ProfileHeader from "@/components/profile/ProfileHeader";
import { ProfileHighlightsRow } from "@/components/feed/StoryStudioExtras";

function ExclusiveTabContent({ profile, navigate, user }) {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState(null);
  const [paidMedia, setPaidMedia] = useState([]);
  const [mySubs, setMySubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [detailAsset, setDetailAsset] = useState(null);
  // iter260 · Video im Detail-Modal abspielbar für Berechtigte.
  const [playingVideo, setPlayingVideo] = useState(false);
  useEffect(() => { setPlayingVideo(false); }, [detailAsset?.asset_id]);
  const isSubscribed = mySubs.some((s) => s.creator_id === profile.person_id && s.status === "ACTIVE");
  const activeTier = (mySubs.find((s) => s.creator_id === profile.person_id && s.status === "ACTIVE") || {}).tier;

  useEffect(() => {
    (async () => {
      try {
        const [tiersRes, mediaRes] = await Promise.allSettled([
          api.get(`/creators/${profile.handle}/subscription-tiers`),
          api.get(`/creators/${profile.handle}/paid-media`),
        ]);
        if (tiersRes.status === "fulfilled") setTiers(tiersRes.value.data || null);
        else setTiers(null);
        if (mediaRes.status === "fulfilled") setPaidMedia(mediaRes.value.data?.assets || []);
        if (user && user !== false) {
          try {
            const { data: sd } = await api.get("/me/subscriptions");
            setMySubs(sd.subscriptions || []);
          } catch { /* ignore */ }
        }
      } finally { setLoading(false); }
    })();
  }, [profile.handle, profile.person_id, user]);

  const [payChoice, setPayChoice] = useState(null); // {kind:'tier'|'asset', ref, amount, title}

  const subscribeTier = async (tierUpper) => {
    if (!user || user === false) { navigate("/login"); return; }
    setError("");
    const price = tiers?.[`${tierUpper.toLowerCase()}_price_eur`];
    setPayChoice({ kind: "tier", ref: tierUpper, amount: price, title: `${tierUpper} · Abo` });
  };
  const purchaseAsset = async (asset_id) => {
    if (!user || user === false) { navigate("/login"); return; }
    setError("");
    const a = paidMedia.find((x) => x.asset_id === asset_id);
    setPayChoice({ kind: "asset", ref: asset_id, amount: a?.price_eur, title: t("public_profile.single_purchase", { defaultValue: "Einzelkauf" }) });
  };
  // iter266 · Einheitlicher Checkout (Stripe/PayPal/Vorkasse) für Abo + Einzelkauf.
  const runCheckout = async (method) => {
    if (!payChoice) return {};
    const origin = window.location.origin;
    const url = payChoice.kind === "tier"
      ? `/creators/${profile.handle}/subscribe/${payChoice.ref}/checkout`
      : `/paid-media/${payChoice.ref}/checkout`;
    const { data } = await api.post(url, { origin_url: origin, payment_method: method });
    if (data.url) { window.location.href = data.url; return { redirect: true }; }
    if (data.mock && (data.session_id || data.tx_id)) {
      await api.post("/payments/mock-confirm", { session_id: data.session_id, tx_id: data.tx_id });
      return { done: true };
    }
    if (data.bank_instructions) return { bank_instructions: data.bank_instructions };
    return {};
  };

  // iter132: Warenkorb-Integration für digitale Einzelkäufe (Social).
  const addAssetToCart = async (asset_id) => {
    if (!user || user === false) { navigate("/login"); return; }
    setError(""); setBusyId(asset_id);
    try {
      await api.post("/cart/paid-media", { asset_id });
      setDetailAsset(null);
      navigate("/cart");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "ALREADY_OWNED") setError(t("public_profile.already_owned"));
      else setError(typeof detail === "string" ? detail : t("public_profile.checkout_error"));
    } finally { setBusyId(null); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[#999]" /></div>;

  return (
    <div data-testid="profile-exklusiv" className="px-4 py-4 space-y-4">
      {error && <p data-testid="profile-exklusiv-error" className="text-xs text-red-500">{error}</p>}
      <PaymentChoiceModal
        open={!!payChoice}
        title={payChoice?.title}
        amount={payChoice?.amount}
        allowedMethods={payChoice?.kind === "tier" ? ["STRIPE", "PAYPAL"] : undefined}
        notice={payChoice?.kind === "tier"
          ? "Dieses Abonnement verlängert sich automatisch alle 30 Tage um weitere 30 Tage. Du kannst es jederzeit im Profil unter „Meine Käufe“ kündigen – es läuft dann noch bis zum Ende der bezahlten Laufzeit. Abos sind nur per Stripe oder PayPal zahlbar."
          : ""}
        onClose={() => { const wasDone = payChoice?._done; setPayChoice(null); if (wasDone) window.location.reload(); }}
        onConfirm={async (method) => {
          const r = await runCheckout(method);
          if (r?.done) setPayChoice((p) => (p ? { ...p, _done: true } : p));
          return r;
        }}
      />
      {isSubscribed && (
        <InfoCard variant="success" testId="profile-exklusiv-active">
          <CheckCircle size={14} /> {t("public_profile.active_sub", { name: profile.display_name })}
        </InfoCard>
      )}
      {/* iter164 · Single-Tier „Premium" — nur wenn Preis gesetzt UND Tier veröffentlicht */}
      {tiers && tiers.published && Number(tiers.premium_price_eur) > 0 && (
        <section data-testid="profile-tiers-section">
          <h3 className="text-xs font-medium text-[#666] uppercase tracking-wider mb-2">{t("public_profile.abos", { defaultValue: "Premium-Abo" })}</h3>
          {(() => {
            const price = Number(tiers.premium_price_eur);
            const isActive = activeTier === "PREMIUM" || isSubscribed;
            const busyThis = busyId === "tier:PREMIUM";
            return (
              <div
                data-testid="profile-tier-premium"
                className="rounded-2xl border-2 border-brand-black bg-white p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
              >
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Premium</p>
                  <p className="text-3xl font-black text-brand-black mt-1">
                    {price.toFixed(2)} €
                    <span className="text-xs font-normal text-[#666] ml-1">{t("public_profile.per_month", { defaultValue: "/ Monat" })}</span>
                  </p>
                  <p className="text-[11px] text-[#666] mt-1">{t("public_profile.monthly_recurring_hint")}</p>
                </div>
                <button
                  data-testid="profile-tier-subscribe-premium"
                  onClick={() => subscribeTier("PREMIUM")}
                  disabled={busyThis || isSubscribed}
                  className={`h-11 px-6 rounded-full text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 transition-colors ${
                    isActive
                      ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                      : "bg-brand-black text-brand-white hover:bg-neutral-800"
                  }`}
                >
                  {busyThis ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {isActive
                    ? t("public_profile.active", { defaultValue: "Aktiv" })
                    : t("public_profile.subscribe", { defaultValue: "Abonnieren" })}
                </button>
              </div>
            );
          })()}
        </section>
      )}
      {paidMedia.length > 0 && (
        <section>
          <h3 className="text-[10px] uppercase tracking-wider text-[#999] font-semibold mb-2">{t("public_profile.one_time")}</h3>
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {paidMedia.map((a) => (
              <div
                key={a.asset_id}
                data-testid={`profile-paid-${a.asset_id}`}
                role="button"
                tabIndex={0}
                onClick={() => setDetailAsset(a)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailAsset(a); } }}
                className="border border-[#E5E5E5] rounded-2xl bg-white overflow-hidden flex flex-col cursor-pointer hover:border-[#0A0A0A]/40 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/20 transition-colors"
              >
                <div className="aspect-square bg-[#F5F5F5] flex items-center justify-center overflow-hidden relative">
                  {(a.owned || a.unlocked || isSubscribed) && (
                    <div
                      data-testid={`profile-paid-owned-badge-${a.asset_id}`}
                      title={t("public_profile.owned_badge_title", { defaultValue: "Bereits im Zugriff" })}
                      className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-md"
                    >
                      <CheckCircle size={14} />
                    </div>
                  )}
                  {a.media_type === "IMAGE" && a.media_url ? (
                    <img
                      src={a.preview_url || a.media_url}
                      alt=""
                      className={`w-full h-full object-cover ${(a.owned || a.unlocked || isSubscribed) ? "" : "blur-sm"}`}
                    />
                  ) : a.media_type === "VIDEO" && a.preview_url && !/\.(mp4|mov|webm|m4v)(\?|$)/i.test(a.preview_url) ? (
                    <img
                      src={a.preview_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : a.media_type === "VIDEO" ? (
                    // iter253 · Video-Kachel-Fallback für gelockte Videos: Gradient + Play-Icon
                    // (Blur-Endpoint liefert für Videos 404). Konsistent zum Detail-Modal.
                    <div data-testid={`profile-paid-video-fallback-${a.asset_id}`} className="w-full h-full bg-gradient-to-br from-[#111] to-[#333] flex items-center justify-center text-white">
                      <div className="w-12 h-12 rounded-full bg-white/25 border border-white/50 flex items-center justify-center backdrop-blur-md">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-[#999]">{t("public_profile.video_label")}{a.duration_sec ? " · " + Math.round(a.duration_sec/60) + " " + t("public_profile.minutes") : ""}</div>
                  )}
                  {!isSubscribed && !a.owned && !a.unlocked && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/30 text-white">
                      <p className="text-[10px] uppercase tracking-wider font-bold">Einzelkauf</p>
                      {/* iter253 · Medium-Label (VIDEO/BILD) in gleichem Style wie „EINZELKAUF", direkt darunter, unabhängig von der Position des Preises. */}
                      <p data-testid={`profile-paid-medium-${a.asset_id}`} className="text-[10px] uppercase tracking-wider font-bold opacity-80">
                        {a.media_type === "VIDEO" ? "Video" : "Bild"}
                      </p>
                      <p className="text-xl font-bold mt-1">{a.price_eur.toFixed(2)} €</p>
                    </div>
                  )}
                </div>
                <div className="p-3 flex flex-col gap-2 flex-1">
                  <p className="text-sm font-semibold text-[#0A0A0A] line-clamp-1">{a.title}</p>
                  {a.description && (
                    <div className="text-xs text-[#666] line-clamp-3 flex-1">
                      <RichTextView html={a.description} />
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <p className="text-sm font-bold text-[#0A0A0A]">{a.price_eur.toFixed(2)} €</p>
                {!isSubscribed && !a.owned && !a.unlocked ? (
                    <button
                      type="button"
                      data-testid={`profile-paid-addcart-${a.asset_id}`}
                      onClick={(e) => { e.stopPropagation(); addAssetToCart(a.asset_id); }}
                      disabled={busyId === a.asset_id}
                      className="rounded-full h-9 px-4 bg-[#0A0A0A] text-white text-xs font-medium disabled:opacity-40 hover:bg-[#222] flex items-center gap-1"
                    >
                      {busyId === a.asset_id ? <Loader2 size={12} className="animate-spin" /> : <ShoppingBag size={12} />}
                      {t("public_profile.add_to_cart", { defaultValue: "In den Warenkorb" })}
                    </button>
                  ) : (
                    <span data-testid={`profile-paid-owned-${a.asset_id}`} className="text-xs text-emerald-700 font-semibold">
                      {t("public_profile.owned_label", { defaultValue: "Bereits im Zugriff" })}
                    </span>
                  )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {(!tiers || !(tiers.standard_price_eur || tiers.premium_price_eur || tiers.vip_price_eur)) && paidMedia.length === 0 && (
        <div className="text-center py-8">
          <Lock size={28} className="mx-auto text-[#CCC]" />
          <p className="mt-3 text-sm font-semibold text-[#0A0A0A]">{t("exclusive.title")}</p>
          <p className="mt-1 text-xs text-[#999]">{t("public_profile.no_exclusive_yet", { name: profile.display_name })}</p>
        </div>
      )}
      {detailAsset && (
        <div
          data-testid="profile-paid-detail-modal"
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setDetailAsset(null)}
        >
          <div
            className="bg-white rounded-2xl overflow-hidden w-full max-w-md max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
              <p className="text-sm font-bold text-[#0A0A0A] truncate">{detailAsset.title}</p>
              <button
                type="button"
                data-testid="profile-paid-detail-close"
                onClick={() => setDetailAsset(null)}
                className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]"
                aria-label={t("common.close", { defaultValue: "Schließen" })}
              >
                <X size={16} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <div className="aspect-square bg-[#F5F5F5] flex items-center justify-center overflow-hidden">
                {detailAsset.media_type === "IMAGE" && detailAsset.media_url ? (
                  <img
                    src={(detailAsset.owned || detailAsset.unlocked || isSubscribed) ? detailAsset.media_url : toBlurUrl(detailAsset.media_url)}
                    alt=""
                    className={`w-full h-full object-contain ${(detailAsset.owned || detailAsset.unlocked || isSubscribed) ? "" : "blur-sm"}`}
                  />
                ) : detailAsset.media_type === "VIDEO" ? (
                  // iter252/iter260 · PPV-Video: Poster-Frame + Play — für Käufer/
                  // Autor/Abonnent abspielbar, sonst gesperrter Poster.
                  (playingVideo && detailAsset.play_url && (detailAsset.owned || detailAsset.unlocked || isSubscribed)) ? (
                    <video
                      data-testid="profile-paid-detail-video-player"
                      src={detailAsset.play_url}
                      controls
                      autoPlay
                      playsInline
                      className="w-full h-full object-contain bg-black"
                    />
                  ) : (
                  <div data-testid="profile-paid-detail-video-poster" className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-[#111] to-[#333] text-white">
                    {detailAsset.preview_url && (
                      <img
                        src={detailAsset.preview_url}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-40 blur-sm"
                      />
                    )}
                    <div className="relative z-10 flex flex-col items-center gap-2">
                      <div className="w-16 h-16 rounded-full bg-white/20 border border-white/40 flex items-center justify-center backdrop-blur-md">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
                      </div>
                      <p className="text-xs font-semibold tracking-wide">{t("public_profile.video_label")}</p>
                      {detailAsset.duration_sec && (
                        <p className="text-[10px] text-white/70">≈ {Math.round(detailAsset.duration_sec/60)} {t("public_profile.minutes")}</p>
                      )}
                    </div>
                    {(detailAsset.owned || detailAsset.unlocked || isSubscribed) && detailAsset.play_url && (
                      <button
                        type="button"
                        data-testid="profile-paid-detail-play-btn"
                        onClick={() => setPlayingVideo(true)}
                        aria-label={t("public_profile.play_video", { defaultValue: "Video abspielen" })}
                        className="absolute inset-0 z-20 cursor-pointer"
                      />
                    )}
                  </div>
                  )
                ) : (
                  <div className="text-xs text-[#999]">
                    {t("public_profile.video_label")}
                    {detailAsset.duration_sec ? " · " + Math.round(detailAsset.duration_sec/60) + " " + t("public_profile.minutes") : ""}
                  </div>
                )}
              </div>
              <div className="p-4 space-y-3">
                <p className="text-lg font-bold text-[#0A0A0A]">{detailAsset.price_eur.toFixed(2)} €</p>
                {detailAsset.description && (
                  <div data-testid="profile-paid-detail-description" className="text-sm text-[#333]">
                    <RichTextView html={detailAsset.description} />
                  </div>
                )}
              </div>
            </div>
            <div className="px-4 py-3 border-t border-[#E5E5E5]">
              {(detailAsset.owned || detailAsset.unlocked || isSubscribed) ? (
                <div
                  data-testid={`profile-paid-detail-owned-${detailAsset.asset_id}`}
                  className="w-full h-11 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 text-sm font-medium flex items-center justify-center gap-2"
                >
                  <CheckCircle size={14} />
                  {t("public_profile.owned_label", { defaultValue: "Bereits im Zugriff" })}
                </div>
              ) : (
                <button
                  type="button"
                  data-testid={`profile-paid-detail-buy-${detailAsset.asset_id}`}
                  onClick={() => addAssetToCart(detailAsset.asset_id)}
                  disabled={busyId === detailAsset.asset_id}
                  className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium disabled:opacity-40 hover:bg-[#222] flex items-center justify-center gap-2"
                >
                  {busyId === detailAsset.asset_id ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
                  {t("public_profile.add_to_cart", { defaultValue: "In den Warenkorb" })}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicProfilePage() {
  const { t } = useTranslation();
  const { handle } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [requested, setRequested] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [posts, setPosts] = useState([]);
  const [listings, setListings] = useState([]);
  const [presence, setPresence] = useState({ MARKETPLACE: false, STILLTOUCHEDX: false, SOCIAL: false });

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: pres }] = await Promise.all([
          api.get(`/profiles/${handle}`),
          api.get(`/profiles/${handle}/area-presence`).catch(() => ({ data: { presence: {} } })),
        ]);
        setProfile(data);
        setPresence(pres.presence || {});
        setFollowing(data.is_following || false);
        setRequested(data.follow_request_pending || false);
        // Determine first available tab (Social: nur posts / exklusiv)
        const caps = data.capabilities || [];
        const isCreator = caps.some(c => c.capability_code === "ST_CREATOR" && c.status === "ACTIVE");
        const hasExclusive = data.has_exclusive_content;
        const validTabs = new Set(["posts"]);
        if (hasExclusive) validTabs.add("exklusiv");
        setActiveTab(initialTab && validTabs.has(initialTab) ? initialTab : (isCreator ? "posts" : hasExclusive ? "exklusiv" : "posts"));
      } catch { setProfile(null); }
      finally { setLoading(false); }
    })();
  }, [handle, initialTab]);

  // Load tab content
  useEffect(() => {
    if (!profile || !activeTab) return;
    if (activeTab === "posts") {
      (async () => {
        try {
          const { data } = await api.get(`/home/feed`);
          // Platform-Posts (author_type="PLATFORM") gehören in den globalen Feed, nicht in das
          // persönliche Profil des Admins, der sie erstellt hat.
          const authorPosts = (data.posts || []).filter(
            p => p.author_id === profile.person_id && p.author_type !== "PLATFORM" && !p?.author?.is_platform
          );
          setPosts(authorPosts);
        } catch { setPosts([]); }
      })();
    } else if (activeTab === "exklusiv") {
      // Exklusiv-Tab lädt seine eigenen Daten im Sub-Component
    }
  }, [profile, activeTab]);

  const toggleFollow = async () => {
    if (!user || user === false || !profile) return;
    try {
      if (following) {
        await api.delete(`/profiles/${profile.person_id}/follow`);
        setFollowing(false);
        setProfile(p => ({
          ...p,
          follower_count: Math.max(0, (p.follower_count || 0) - 1),
          // Privates Profil → nach Unfollow sofort wieder sperren
          is_content_locked: !!p.social_private,
        }));
      } else if (requested) {
        // Offene Anfrage zurückziehen
        await api.delete(`/profiles/${profile.person_id}/follow`);
        setRequested(false);
      } else {
        const { data } = await api.post(`/profiles/${profile.person_id}/follow`);
        if (data?.status === "requested") {
          setRequested(true);
        } else {
          setFollowing(true);
          setProfile(p => ({ ...p, follower_count: (p.follower_count || 0) + 1 }));
        }
      }
    } catch { /* ignore */ }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;
  if (!profile) return <div className="px-4 py-16 text-center text-sm text-[#999]">{t("public_profile.not_found")}</div>;

  const caps = profile.capabilities || [];
  const isCreator = caps.some(c => c.capability_code === "ST_CREATOR" && c.status === "ACTIVE");
  const isMpSeller = caps.some(c => c.capability_code === "MP_SELLER" && c.status === "ACTIVE");
  const isStxSeller = caps.some(c => c.capability_code === "STX_SELLER" && c.status === "ACTIVE");

  // iter212 · Social-Profil nur für ST_CREATOR sichtbar. Für Nicht-Creator wird
  // automatisch auf STX bzw. Marketplace umgeleitet, damit sie überhaupt eine
  // öffentliche Präsenz haben. Owner-Vorgabe 2026-02-28.
  if (!isCreator) {
    if (isStxSeller) return <Navigate to={`/user/${handle}/stx`} replace />;
    if (isMpSeller) return <Navigate to={`/user/${handle}/marketplace`} replace />;
    return <div className="px-4 py-16 text-center text-sm text-[#999]" data-testid="public-profile-not-creator">{t("public_profile.not_found")}</div>;
  }

  // Build tabs dynamically — iter88.8 Social-Only: KEIN Shop, KEIN Info-Tab (bereichsspezifische Profile via /marketplace|/stx)
  // iter174g · „Beiträge" → „Öffentlich" (User-Vorgabe 2026-02-28) + Count-Suffix in Klammern.
  const publicCount = Number(profile.public_post_count ?? profile.post_count ?? 0);
  const exclusiveCount = Number(profile.exclusive_post_count ?? 0);
  const tabs = [];
  if (isCreator && profile.post_count > 0) {
    tabs.push({ key: "posts", label: `${t("public_profile.posts")} (${publicCount})` });
  }
  // Exklusiv-Tab wenn Creator hat exklusive Inhalte (Adult-Posts / Paid Media / Abos).
  if (profile.has_exclusive_content) {
    tabs.push({ key: "exklusiv", label: `${t("public_profile.exclusive")} (${exclusiveCount})` });
  }
  if (tabs.length === 0) tabs.push({ key: "posts", label: `${t("public_profile.posts")} (0)` }); // Fallback

  return (
    <div data-testid="public-profile-page">
      <ProfileHeader
        profile={profile}
        area="SOCIAL"
        itemCount={profile.post_count || 0}
        isOwn={!!(user && user !== false && (user._id || user.id) === profile.person_id)}
        isFollowing={following}
        followRequested={requested}
        onFollow={toggleFollow}
        presence={presence}
        navigate={navigate}
        theme="light"
      />

      {/* iter322 · Highlights-Reihe (öffentlich; privat-Profil serverseitig gegated) */}
      <ProfileHighlightsRow profileId={profile.person_id} />

      {/* iter212 · STX-Steckbrief NUR im STX-Profil (`/user/:handle/stx`) — nicht mehr
          im Social-Profil. Owner-Vorgabe 2026-02-28. */}
      {profile.is_content_locked ? (
        <div data-testid="profile-locked" className="px-6 py-16 flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-[#F0F0F0] flex items-center justify-center mb-4">
            <Lock size={24} className="text-[#999]" />
          </div>
          <p className="text-base font-semibold text-[#0A0A0A]">
            {t("public_profile.private_title", { defaultValue: "Dieses Profil ist privat" })}
          </p>
          <p className="mt-1.5 max-w-xs text-sm text-[#777] leading-relaxed">
            {t("public_profile.private_desc", { defaultValue: "Folge diesem Profil und warte auf die Bestätigung, um die Beiträge zu sehen." })}
          </p>
          <button
            data-testid="profile-locked-follow"
            onClick={toggleFollow}
            className={`mt-5 h-10 px-6 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${
              requested ? "bg-white border border-[#E5E5E5] text-[#0A0A0A]" : "bg-[#0A0A0A] text-white hover:bg-[#222]"
            }`}
          >
            {requested
              ? <><Clock size={15} /> {t("public_profile.requested", { defaultValue: "Angefragt" })}</>
              : <><UserPlus size={15} /> {t("public_profile.follow", { defaultValue: "Folgen" })}</>}
          </button>
        </div>
      ) : (
      <>
      {/* Dynamic Tabs */}
      <TabBar
        tabs={tabs.map((tabDef) => ({
          key: tabDef.key,
          label: tabDef.label,
          testId: `profile-tab-${tabDef.key}`,
        }))}
        activeKey={activeTab}
        onChange={setActiveTab}
      />

      {/* Tab Content */}
      {activeTab === "posts" && (
        <div data-testid="profile-posts">
          {posts.length === 0 ? (
            <p className="px-4 py-12 text-center text-sm text-[#999]">{t("public_profile.no_posts")}</p>
          ) : (
            posts.map((p) => {
              // iter164 · Highlight-Ring wenn Query-Param ?post=... matcht (Deep-Link aus Admin).
              const highlightId = searchParams.get("post");
              const isHighlighted = highlightId && highlightId === p.post_id;
              return (
                <div
                  key={p.post_id}
                  id={`post-${p.post_id}`}
                  ref={isHighlighted ? (el) => { if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 200); } : undefined}
                  className={isHighlighted ? "ring-4 ring-brand-silver rounded-2xl transition-all" : ""}
                >
                  <FeedCard post={p} />
                </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === "exklusiv" && <ExclusiveTabContent profile={profile} navigate={navigate} user={user} />}
      </>
      )}
    </div>
  );
}
